import React, { useState } from "react";
import { ChevronDown, ChevronRight, MapPin, FileText } from "lucide-react";

// P5 — WHOLE-ESSAY ORIENTATION during local Composition. The student focuses on ONE paragraph at a time
// (functional_v3), but never loses the whole: this compact, collapsible panel shows the essay as
// Introduction / Body / Conclusion -> sections, marks WHERE THEY ARE, names what the current section is
// doing for the reader, and lets them read the whole assembled draft. Semantic labels (no bare indentation),
// so the student always knows what structural level they are on. OT is the authority; this is a read-only view.

const GROUPS = [
  ["introduction", "Introduction"],
  ["body", "Body"],
  ["conclusion", "Conclusion"],
];

const REL_LABEL = {
  clear: "clearly develops your main idea",
  probable: "develops your main idea",
  ambiguous: "its link to your main idea is still open",
  none: "",
};

const funcOf = (s) => {
  const f = (s.section_function || "body").toLowerCase();
  return ["introduction", "body", "conclusion"].includes(f) ? f : "body";
};

export const EssayOrientation = ({ cursor }) => {
  const [open, setOpen] = useState(true);
  const [showWhole, setShowWhole] = useState(false);
  if (!cursor || !Array.isArray(cursor.outline) || cursor.outline.length === 0) return null;

  const outline = cursor.outline;
  // UNIFIED entry: a direct draft arrives as ONE unorganized section. With nothing known about the structure
  // and only one paragraph there is nothing to orient — render nothing (state-driven, not route-driven).
  const structureKnown = !!(cursor.thesis || "").trim()
    || outline.some((s) => (s.title || s.section_function || s.reader_need || s.section_relation_to_thesis));
  if (!structureKnown && (cursor.total ?? outline.length) <= 1) return null;
  const nameOf = (s) => s.title || (s.section_function
    ? (funcOf(s) === "introduction" ? "the introduction" : funcOf(s) === "conclusion" ? "the conclusion" : "a body section")
    : "your draft");
  const current = cursor.current || {};
  const curSectionIdx = current.section_index ?? 0;
  const thesis = (cursor.thesis || "").trim();
  const cur = outline[curSectionIdx] || {};
  const relText = REL_LABEL[(cur.section_relation_to_thesis || "").toLowerCase()] || "";

  // section index within the full outline (for the WORKING-HERE marker) and grouping by function
  const withIdx = outline.map((s, i) => ({ ...s, _i: i }));
  const groups = GROUPS.map(([key, label]) => [label, key, withIdx.filter((s) => funcOf(s) === key)])
    .filter(([, , arr]) => arr.length > 0);

  return (
    <div className="mt-5 rounded-lg border border-stone-200 bg-[#faf9f6] overflow-hidden" data-testid="essay-orientation">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors hover:bg-stone-100/60"
        data-testid="essay-orientation-toggle"
      >
        <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          <MapPin className="h-3.5 w-3.5 text-amber-700" /> Your essay
        </span>
        <span className="flex items-center gap-2 text-[12px] text-stone-600">
          <span className="hidden sm:inline">
            Working on{" "}
            <span className="font-semibold text-stone-800">
              {nameOf(cur)}
            </span>
          </span>
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
      </button>

      {open && (
        <div className="border-t border-stone-200 px-4 py-3">
          {thesis && (
            <div className="mb-3 rounded-md bg-white border border-stone-200 px-3 py-2" data-testid="essay-orientation-thesis">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">Main idea</div>
              <div className="mt-0.5 text-[13px] leading-snug text-stone-700">{thesis}</div>
            </div>
          )}

          <div className="space-y-3">
            {groups.map(([label, key, arr]) => (
              <div key={key}>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">
                  {key === "body" && arr.every((s) => !s.section_function) ? "Your writing" : label}
                </div>
                <ul className="mt-1 space-y-1">
                  {arr.map((s) => {
                    const isCurrent = s._i === curSectionIdx;
                    return (
                      <li
                        key={s.section_id}
                        data-testid={`essay-orientation-section-${s.section_id}`}
                        className={`rounded-md px-2.5 py-1.5 text-[13px] leading-snug ${
                          isCurrent ? "bg-amber-50 border border-amber-300 text-stone-800" : "text-stone-600"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-1.5">
                            <span className="text-[9px] font-semibold uppercase tracking-wide text-stone-400">
                              {!s.section_function && !s.title ? "Draft" : key === "body" ? "Section" : label}
                            </span>
                            <span className={isCurrent ? "font-semibold" : ""}>
                              {s.title || (!s.section_function ? "Your draft (not yet organized into sections)" : "(untitled part)")}
                            </span>
                          </span>
                          {isCurrent && (
                            <span className="shrink-0 rounded-full bg-amber-600 px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-white">
                              Working here
                            </span>
                          )}
                        </div>
                        {isCurrent && (
                          <div className="mt-1 space-y-0.5 text-[11.5px] text-stone-500">
                            {(current.paragraph_count ?? 1) > 1 && (() => {
                              // INCREMENT 4: the working object is the FUNCTIONAL UNIT — its span of paragraphs, not one paragraph at a time
                              const first = (current.paragraph_index ?? 0) + 1;
                              const n = current.unit_paragraph_count ?? 1;
                              const last = first + n - 1;
                              return (
                                <div data-testid="essay-orientation-focus">
                                  Working on{" "}
                                  <span className="font-semibold text-stone-700">
                                    {n > 1 ? `paragraphs ${first}\u2013${last}` : `paragraph ${first}`} of {current.paragraph_count}
                                  </span>{" "}
                                  in this section
                                </div>
                              );
                            })()}
                            {relText && <div>This section {relText}.</div>}
                            {cur.reader_need && <div>Reader needs here: {cur.reader_need}.</div>}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          {(cursor.full_draft || "").trim() && (
            <div className="mt-3 border-t border-stone-200 pt-2">
              <button
                type="button"
                onClick={() => setShowWhole((v) => !v)}
                className="flex items-center gap-1.5 text-[12px] font-medium text-amber-800 hover:text-amber-900"
                data-testid="essay-orientation-whole-toggle"
              >
                <FileText className="h-3.5 w-3.5" />
                {showWhole ? "Hide the whole draft" : "Read the whole draft"}
              </button>
              {showWhole && (
                <div
                  className="mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md bg-white border border-stone-200 px-3 py-2 text-[13px] leading-relaxed text-stone-700"
                  data-testid="essay-orientation-whole"
                >
                  {cursor.full_draft}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EssayOrientation;
